import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Buildings, FloppyDisk, Globe, Copy, WhatsappLogo, MapPin, Phone, ImageSquare, UploadSimple, Trash, Sparkle, Bell,
} from "@phosphor-icons/react";
import QrGenerator from "./components/QrGenerator";
import OpeningHoursDesigner from "./components/OpeningHoursDesigner";
import ActiveSessionsCard from "../components/security/ActiveSessionsCard";
import ImageCropDialog from "./components/ImageCropDialog";
import { PasskeysCard } from "../components/PasskeysCard";
import { SettingsExtensions } from "../components/SettingsExtensions";
import { GestorMultiOrgCards } from "../components/GestorMultiOrgCards";
import { NotificationPreferencesCard } from "../components/notifications/NotificationPreferencesCard";

const DAY_LABELS = ["L", "M", "X", "J", "V", "S", "D"];
const MAX_IMG_BYTES = 1024 * 1024 * 2; // 2 MB

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function Settings() {
  const { org, user, refresh, isSolo } = useAuth();
  const [form, setForm] = useState({
    name: "", slug: "", nif: "", description: "",
    address: "", city: "", locality: "", phone: "", google_maps_url: "",
    instagram_url: "", facebook_url: "", google_place_id: "",
    logo_url: "", hero_image_url: "", gallery: [], brand_color: "#0033A0",
    commission_titular: 40, commission_colaborador: 60,
    fixed_monthly_costs: 1050, notification_email: "",
    working_hours_start: "09:00", working_hours_end: "20:00",
    working_days: [0, 1, 2, 3, 4, 5],
    opening_hours_per_day: {},
    whatsapp_reminders_enabled: false,
    sectors: [],
    requires_review_sectors: [],
    vacation_mode_active: false,
    vacation_message: "",
    vacation_until: "",
    offer_pickup_service: false,
    pickup_extra_fee: 0,
    pickup_radius_km: 10,
    gestor_email: "",
    gestor_name: "",
  });
  const [avatarUrl, setAvatarUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [sectorsList, setSectorsList] = useState([]);
  // Sprint D · #9 · Crop/Resize de imágenes
  const [cropState, setCropState] = useState({ open: false, src: "", aspect: 1, target: null, title: "" });

  useEffect(() => {
    api.get("/catalog/sectors").then(({ data }) => setSectorsList(data.sectors || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (org) setForm({
      name: org.name || "", slug: org.slug || "", nif: org.nif || "",
      description: org.description || "",
      address: org.address || "", city: org.city || "", locality: org.locality || "",
      phone: org.phone || "", google_maps_url: org.google_maps_url || "",
      instagram_url: org.instagram_url || "", facebook_url: org.facebook_url || "",
      google_place_id: org.google_place_id || "",
      logo_url: org.logo_url || "", hero_image_url: org.hero_image_url || "",
      gallery: org.gallery || [], brand_color: org.brand_color || "#0033A0",
      commission_titular: org.commission_titular ?? 40,
      commission_colaborador: org.commission_colaborador ?? 60,
      fixed_monthly_costs: org.fixed_monthly_costs ?? 1050,
      notification_email: org.notification_email || "",
      working_hours_start: org.working_hours_start || "09:00",
      working_hours_end: org.working_hours_end || "20:00",
      working_days: org.working_days || [0, 1, 2, 3, 4, 5],
      opening_hours_per_day: org.opening_hours_per_day || {},
      whatsapp_reminders_enabled: !!(org.whatsapp_reminders_enabled ?? org.sms_reminders_enabled),
      sectors: org.sectors || [],
      requires_review_sectors: org.requires_review_sectors || [],
      vacation_mode_active: !!org.vacation_mode_active,
      vacation_message: org.vacation_message || "",
      vacation_until: org.vacation_until || "",
      offer_pickup_service: !!org.offer_pickup_service,
      pickup_extra_fee: org.pickup_extra_fee ?? 0,
      pickup_radius_km: org.pickup_radius_km ?? 10,
      gestor_email: org.gestor_email || "",
      gestor_name: org.gestor_name || "",
    });
    if (user) setAvatarUrl(user.avatar_url || "");
  }, [org, user]);

  const onTit = (v) => setForm({ ...form, commission_titular: v, commission_colaborador: 100 - Number(v) });
  const onCol = (v) => setForm({ ...form, commission_colaborador: v, commission_titular: 100 - Number(v) });

  const toggleDay = (d) => {
    const has = form.working_days.includes(d);
    setForm({ ...form, working_days: has ? form.working_days.filter((x)=>x!==d) : [...form.working_days, d].sort() });
  };

  const handleImage = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMG_BYTES) {
      toast.error("Imagen demasiado grande (máx 2 MB)");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Selecciona un archivo de imagen");
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    const aspect = target === "hero_image_url" ? 2 : 1; // hero 2:1 · logo 1:1
    const title = target === "hero_image_url" ? "Recortar foto del local (2:1)" : "Recortar logo (cuadrado)";
    setCropState({ open: true, src: dataUrl, aspect, target, title });
    e.target.value = "";
  };

  const onCropConfirm = async (croppedDataUrl) => {
    const { target } = cropState;
    if (!target) return;
    if (target === "avatar") {
      setCropState({ open: false, src: "", aspect: 1, target: null, title: "" });
      await saveAvatar(croppedDataUrl);
      return;
    }
    if (target.startsWith("gallery:")) {
      setForm((f) => ({ ...f, gallery: [...f.gallery, croppedDataUrl] }));
      toast.success("Imagen añadida a la galería");
    } else {
      setForm((f) => ({ ...f, [target]: croppedDataUrl }));
      toast.success("Imagen recortada");
    }
    setCropState({ open: false, src: "", aspect: 1, target: null, title: "" });
  };

  const onCropCancel = () => setCropState({ open: false, src: "", aspect: 1, target: null, title: "" });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/org/me", {
        name: form.name, slug: form.slug, nif: form.nif,
        description: form.description,
        address: form.address, city: form.city, locality: form.locality,
        phone: form.phone, google_maps_url: form.google_maps_url,
        instagram_url: form.instagram_url, facebook_url: form.facebook_url,
        google_place_id: form.google_place_id,
        logo_url: form.logo_url, hero_image_url: form.hero_image_url,
        gallery: form.gallery, brand_color: form.brand_color,
        commission_titular: Number(form.commission_titular),
        commission_colaborador: Number(form.commission_colaborador),
        fixed_monthly_costs: Number(form.fixed_monthly_costs),
        notification_email: form.notification_email,
        working_hours_start: form.working_hours_start,
        working_hours_end: form.working_hours_end,
        working_days: form.working_days,
        opening_hours_per_day: form.opening_hours_per_day || {},
        whatsapp_reminders_enabled: form.whatsapp_reminders_enabled,
        sectors: form.sectors,
        requires_review_sectors: form.requires_review_sectors || [],
        vacation_mode_active: form.vacation_mode_active,
        vacation_message: form.vacation_message,
        vacation_until: form.vacation_until,
        offer_pickup_service: form.offer_pickup_service,
        pickup_extra_fee: Number(form.pickup_extra_fee) || 0,
        pickup_radius_km: Number(form.pickup_radius_km) || 10,
        gestor_email: form.gestor_email || "",
        gestor_name: form.gestor_name || "",
      });
      toast.success("Centro actualizado");
      // Sprint H Batch 4 · trigger marketplace real-time refresh
      try { window.dispatchEvent(new CustomEvent("ag_org_updated")); } catch (e) {}
      try { new BroadcastChannel("ag_org").postMessage({ type: "updated" }); } catch (e) {}
      refresh();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveAvatar = async (url) => {
    try {
      await api.put("/users/me", { avatar_url: url });
      setAvatarUrl(url);
      toast.success(url ? "Foto de perfil actualizada" : "Foto de perfil eliminada");
      refresh();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const onAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMG_BYTES) { toast.error("Imagen demasiado grande (máx 2 MB)"); return; }
    if (!file.type.startsWith("image/")) { toast.error("Selecciona un archivo de imagen"); return; }
    const dataUrl = await fileToDataUrl(file);
    setCropState({ open: true, src: dataUrl, aspect: 1, target: "avatar", title: "Recortar foto de perfil (cuadrada)" });
    e.target.value = "";
  };

  const bookingUrl = `${window.location.origin}/reservar/${form.slug || org?.slug || "tu-centro"}`;
  const copyUrl = () => { navigator.clipboard.writeText(bookingUrl); toast.success("URL copiada al portapapeles"); };

  return (
    <div className="space-y-6 max-w-3xl">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">Configuración</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-1 text-[var(--ag-text)]">Perfil del centro</h1>
      </header>

      {/* Personal avatar */}
      <div className="ag-card p-6">
        <div className="flex items-center gap-2 text-sm text-[var(--ag-text-dim)] mb-3"><Sparkle weight="duotone" /> Mi foto de perfil</div>
        <div className="flex items-center gap-5">
          {avatarUrl ? (
            <img src={avatarUrl} alt="avatar" className="w-20 h-20 rounded-full object-cover border-2 border-[var(--ag-gold)]/40" data-testid="my-avatar" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-[var(--ag-midnight)] border-2 border-[var(--ag-border)] grid place-items-center font-display font-black text-2xl text-[var(--ag-gold)]" data-testid="my-avatar-empty">
              {user?.name?.charAt(0).toUpperCase() || "?"}
            </div>
          )}
          <div className="flex-1">
            <p className="text-sm text-[var(--ag-text)]">{user?.name}</p>
            <p className="text-xs text-[var(--ag-text-dim)] mt-0.5">Se mostrará en la página pública si los clientes pueden seleccionar operario.</p>
            <div className="flex gap-2 mt-3">
              <label className="cursor-pointer">
                <input type="file" accept="image/*" onChange={onAvatarFile} className="hidden" data-testid="avatar-input" />
                <span className="ag-btn-aqua px-3 py-2 text-xs inline-flex items-center gap-1.5"><UploadSimple size={13} weight="bold" /> Subir foto</span>
              </label>
              {avatarUrl && (
                <Button variant="outline" size="sm" onClick={()=>saveAvatar("")} className="rounded-sm" data-testid="avatar-remove">
                  <Trash size={13} className="mr-1" /> Quitar
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Identidad + logo */}
      <div className="ag-card p-6 space-y-4">
        <div className="flex items-center gap-2 text-sm text-[var(--ag-text-dim)]"><Buildings weight="duotone" /> Identidad del centro</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
          <div className="sm:col-span-1 space-y-2">
            <Label className="text-xs uppercase tracking-wider">Logo</Label>
            {form.logo_url ? (
              <div className="relative w-full aspect-square bg-white rounded border border-[var(--ag-border)] grid place-items-center p-3" data-testid="logo-preview">
                <img src={form.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                <button type="button" onClick={()=>setForm({...form, logo_url: ""})}
                  className="absolute top-1 right-1 bg-red-500 text-white w-6 h-6 rounded-full grid place-items-center hover:bg-red-600" data-testid="logo-remove">
                  <Trash size={12} />
                </button>
              </div>
            ) : (
              <label className="cursor-pointer block">
                <input type="file" accept="image/*" onChange={(e)=>handleImage(e,"logo_url")} className="hidden" data-testid="logo-input" />
                <div className="w-full aspect-square border-2 border-dashed border-[var(--ag-border)] rounded grid place-items-center text-center p-4 hover:border-[var(--ag-gold)] transition-colors">
                  <div>
                    <ImageSquare size={28} weight="duotone" className="mx-auto text-[var(--ag-text-dim)]" />
                    <div className="text-xs text-[var(--ag-text-dim)] mt-2">Subir logo</div>
                    <div className="text-[10px] text-[var(--ag-text-dim)] mt-1">Máx 2 MB · PNG / JPG</div>
                  </div>
                </div>
              </label>
            )}
          </div>
          <div className="sm:col-span-2 space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Nombre del centro *</Label>
              <Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="mt-1 rounded-sm" data-testid="set-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">NIF / CIF</Label>
                <Input value={form.nif} onChange={(e)=>setForm({...form, nif: e.target.value})} className="mt-1 rounded-sm font-mono-tab" data-testid="set-nif" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider flex items-center gap-1"><Phone size={11} /> Teléfono público</Label>
                <Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} className="mt-1 rounded-sm" placeholder="+34 600 123 456" data-testid="set-phone" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Descripción breve</Label>
              <Input value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})}
                className="mt-1 rounded-sm" placeholder="Especialistas en detallado integral en Sevilla Sur" data-testid="set-description" />
            </div>
            {/* Color corporativo */}
            <div>
              <Label className="text-xs uppercase tracking-wider flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-sm border border-[var(--ag-border)]" style={{ backgroundColor: form.brand_color }} /> Color corporativo
              </Label>
              <div className="mt-1 flex items-center gap-2">
                <input type="color" value={form.brand_color}
                  onChange={(e)=>setForm({...form, brand_color: e.target.value.toUpperCase()})}
                  className="h-10 w-14 rounded-sm border border-[var(--ag-border)] bg-transparent cursor-pointer"
                  data-testid="set-brand-color-picker" />
                <Input value={form.brand_color}
                  onChange={(e)=>{
                    const v = e.target.value.trim().toUpperCase();
                    if (v === "" || /^#[0-9A-F]{0,6}$/i.test(v)) setForm({...form, brand_color: v});
                  }}
                  className="rounded-sm font-mono-tab uppercase max-w-[140px]"
                  placeholder="#0033A0" maxLength={7}
                  data-testid="set-brand-color-hex" />
                <div className="flex gap-1 ml-1" role="radiogroup" aria-label="Presets">
                  {["#FFB703","#0033A0","#0077B6","#10B981","#E11D48","#7C3AED","#1A1A1A"].map((preset) => (
                    <button key={preset} type="button" onClick={()=>setForm({...form, brand_color: preset})}
                      className={`w-7 h-7 rounded-sm border-2 transition-transform hover:scale-110 ${form.brand_color === preset ? "border-white shadow-lg ring-1 ring-[var(--ag-gold)]" : "border-[var(--ag-border)]"}`}
                      style={{ backgroundColor: preset }}
                      title={preset}
                      data-testid={`set-brand-preset-${preset.replace('#','')}`}
                      aria-label={`Usar color ${preset}`} aria-checked={form.brand_color === preset} role="radio" />
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-[var(--ag-text-dim)] mt-1.5">
                Color de acento de tu marca. Se aplica en la barra superior de tus PDFs (tickets, facturas, AEAT) y en tu perfil público del marketplace.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Sprint F · Recogida a domicilio */}
      <div className="ag-card p-6 space-y-3" data-testid="set-pickup-card">
        <div className="flex items-center gap-2 text-sm text-[var(--ag-text-dim)]"><MapPin weight="duotone" /> Recogida a domicilio</div>
        <p className="text-xs text-[var(--ag-text-dim)]">
          Activa este servicio para ofrecer a tus clientes que recojas su vehículo en una dirección concreta.
          Aparecerá como opción extra en el flujo de reserva pública.
        </p>
        <label className="flex items-center gap-3 cursor-pointer p-3 bg-[var(--ag-midnight)] border border-[var(--ag-border)] rounded-sm">
          <input type="checkbox"
            checked={form.offer_pickup_service}
            onChange={(e)=>setForm({...form, offer_pickup_service: e.target.checked})}
            className="accent-amber-500 w-4 h-4"
            data-testid="pickup-toggle"
          />
          <div className="flex-1">
            <div className="text-sm font-medium">Ofrecer recogida a domicilio</div>
            <div className="text-[10px] text-[var(--ag-text-dim)]">El cliente puede solicitar recogida desde el flujo público.</div>
          </div>
        </label>
        {form.offer_pickup_service && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Tarifa extra (€)</Label>
              <Input type="number" min="0" step="0.5" value={form.pickup_extra_fee}
                onChange={(e)=>setForm({...form, pickup_extra_fee: e.target.value})}
                className="mt-1 rounded-sm tabular-nums" data-testid="pickup-fee" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Radio cobertura (km)</Label>
              <Input type="number" min="1" value={form.pickup_radius_km}
                onChange={(e)=>setForm({...form, pickup_radius_km: e.target.value})}
                className="mt-1 rounded-sm tabular-nums" data-testid="pickup-radius" />
            </div>
          </div>
        )}
      </div>

      {/* Banner vacaciones — cierre temporal */}
      <div className="ag-card p-6 space-y-3" data-testid="set-vacation-card">
        <div className="flex items-center gap-2 text-sm text-[var(--ag-text-dim)]"><Bell weight="duotone" /> Modo vacaciones</div>
        <p className="text-xs text-[var(--ag-text-dim)]">
          Activa esta opción para cerrar tu agenda temporalmente. Los clientes verán un aviso en
          tu perfil del Marketplace y no podrán reservar nuevas citas hasta que lo desactives.
        </p>
        <label className="flex items-center gap-3 cursor-pointer p-3 bg-[var(--ag-midnight)] border border-[var(--ag-border)] rounded-sm">
          <input type="checkbox"
            checked={form.vacation_mode_active}
            onChange={(e)=>setForm({...form, vacation_mode_active: e.target.checked})}
            className="accent-amber-500 w-4 h-4"
            data-testid="vacation-mode-toggle"
          />
          <div className="flex-1">
            <div className="text-sm font-medium">Cerrar reservas (vacaciones)</div>
            <div className="text-[10px] text-[var(--ag-text-dim)]">Aparece banner "En vacaciones" en el Marketplace y se bloquean los slots públicos.</div>
          </div>
          {form.vacation_mode_active && (
            <span className="text-[9px] uppercase tracking-wider bg-amber-500 text-white px-2 py-0.5 rounded font-bold animate-pulse">ACTIVO</span>
          )}
        </label>
        {form.vacation_mode_active && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Mensaje al cliente</Label>
              <Input value={form.vacation_message}
                onChange={(e)=>setForm({...form, vacation_message: e.target.value})}
                placeholder="Vacaciones del 1 al 15 de agosto"
                className="mt-1 rounded-sm"
                data-testid="vacation-message" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Reabrimos el</Label>
              <Input type="date" value={form.vacation_until}
                onChange={(e)=>setForm({...form, vacation_until: e.target.value})}
                className="mt-1 rounded-sm"
                data-testid="vacation-until" />
            </div>
          </div>
        )}
      </div>

      {/* Subsectores que opera el centro */}
      <div className="ag-card p-6 space-y-4" data-testid="set-sectors-card">
        <div className="flex items-center gap-2 text-sm text-[var(--ag-text-dim)]"><Sparkle weight="duotone" /> Subsectores del ecosistema</div>
        <p className="text-xs text-[var(--ag-text-dim)]">
          Marca todos los subsectores que opera tu centro. Aparecerás en el Marketplace cuando
          un cliente busque cualquiera de ellos. Puedes marcar varios si eres polifuncional.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {sectorsList.map((s) => {
            const checked = (form.sectors || []).includes(s.id);
            return (
              <label key={s.id}
                className={`flex items-start gap-2 p-2.5 border rounded-sm cursor-pointer transition-colors ${checked ? "bg-[var(--ag-aqua)]/15 border-[var(--ag-aqua)]/50" : "bg-[var(--ag-midnight)] border-[var(--ag-border)] hover:border-[var(--ag-gold)]/40"}`}
                data-testid={`sector-toggle-${s.id}`}>
                <input type="checkbox"
                  checked={checked}
                  onChange={()=>{
                    const cur = new Set(form.sectors || []);
                    if (cur.has(s.id)) cur.delete(s.id); else cur.add(s.id);
                    setForm({...form, sectors: Array.from(cur)});
                  }}
                  className="mt-0.5 accent-[var(--ag-aqua)]"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[var(--ag-text)]">{s.label_es}</div>
                  <div className="text-[10px] text-[var(--ag-text-dim)] leading-tight mt-0.5">{s.description_es}</div>
                </div>
              </label>
            );
          })}
        </div>
        {(form.sectors || []).length > 0 && (
          <div className="text-[10px] uppercase tracking-wider text-[var(--ag-aqua-soft)]" data-testid="sectors-count">
            {form.sectors.length} subsector{form.sectors.length === 1 ? "" : "es"} seleccionado{form.sectors.length === 1 ? "" : "s"}
          </div>
        )}
      </div>

      {/* Sprint E · #10 · Sectores con validación manual del titular */}
      {(form.sectors || []).length > 0 && (
        <div className="ag-card p-6 space-y-4" data-testid="set-review-sectors-card">
          <div className="flex items-center gap-2 text-sm text-[var(--ag-text-dim)]">
            <Bell weight="duotone" /> Reservas que requieren validación manual
          </div>
          <p className="text-xs text-[var(--ag-text-dim)]">
            De los subsectores activos arriba, marca los que <b className="text-[var(--ag-text)]">necesitan tu aprobación</b> antes
            de confirmarse al cliente (p.ej. cristalería, mecánica con piezas). Las reservas online
            quedarán en estado "Pendiente de validación" y recibirás una notificación push para Aceptar,
            Rechazar o Solicitar más info por chat.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {sectorsList
              .filter((s) => (form.sectors || []).includes(s.id))
              .map((s) => {
                const checked = (form.requires_review_sectors || []).includes(s.id);
                return (
                  <label key={`review-${s.id}`}
                    className={`flex items-start gap-2 p-2.5 border rounded-sm cursor-pointer transition-colors ${checked ? "bg-orange-500/15 border-orange-400/60" : "bg-[var(--ag-midnight)] border-[var(--ag-border)] hover:border-orange-300/50"}`}
                    data-testid={`review-sector-toggle-${s.id}`}>
                    <input type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const cur = new Set(form.requires_review_sectors || []);
                        if (cur.has(s.id)) cur.delete(s.id); else cur.add(s.id);
                        setForm({ ...form, requires_review_sectors: Array.from(cur) });
                      }}
                      className="mt-0.5 accent-orange-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[var(--ag-text)]">{s.label_es}</div>
                      <div className="text-[10px] text-[var(--ag-text-dim)] leading-tight mt-0.5">Requiere validación manual</div>
                    </div>
                  </label>
                );
              })}
          </div>
          {(form.requires_review_sectors || []).length === 0 && (
            <div className="text-[11px] text-[var(--ag-text-dim)] italic">Ninguno seleccionado · todas las reservas se confirman automáticamente.</div>
          )}
        </div>
      )}

      {/* Ubicación */}
      <div className="ag-card p-6 space-y-4">
        <div className="flex items-center gap-2 text-sm text-[var(--ag-text-dim)]"><MapPin weight="duotone" /> Ubicación del local</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider">Dirección completa</Label>
            <Input value={form.address} onChange={(e)=>setForm({...form, address: e.target.value})}
              className="mt-1 rounded-sm" placeholder="Av. de la Paz 12, Local 3" data-testid="set-address" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Ciudad</Label>
            <Input value={form.city} onChange={(e)=>setForm({...form, city: e.target.value})}
              className="mt-1 rounded-sm" placeholder="Sevilla" data-testid="set-city" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Localidad / Provincia</Label>
            <Input value={form.locality} onChange={(e)=>setForm({...form, locality: e.target.value})}
              className="mt-1 rounded-sm" placeholder="Sevilla Sur — Bellavista" data-testid="set-locality" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider">Enlace de Google Maps</Label>
            <Input value={form.google_maps_url} onChange={(e)=>setForm({...form, google_maps_url: e.target.value})}
              className="mt-1 rounded-sm font-mono-tab text-xs" placeholder="https://maps.google.com/?q=..." data-testid="set-maps" />
            <p className="text-[11px] text-[var(--ag-text-dim)] mt-1">Pega el enlace para compartir que te da Google Maps. Tus clientes lo verán como botón "Cómo llegar" en la página de reserva.</p>
          </div>
        </div>
      </div>

      {/* Hero image */}
      <div className="ag-card p-6 space-y-4">
        <div className="flex items-center gap-2 text-sm text-[var(--ag-text-dim)]"><ImageSquare weight="duotone" /> Foto destacada del local (hero image)</div>
        {form.hero_image_url ? (
          <div className="relative" data-testid="hero-preview">
            <img src={form.hero_image_url} alt="Hero" className="w-full h-48 object-cover rounded border border-[var(--ag-border)]" />
            <button type="button" onClick={()=>setForm({...form, hero_image_url: ""})}
              className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 rounded grid place-items-center hover:bg-red-600" data-testid="hero-remove">
              <Trash size={14} />
            </button>
          </div>
        ) : (
          <label className="cursor-pointer block">
            <input type="file" accept="image/*" onChange={(e)=>handleImage(e,"hero_image_url")} className="hidden" data-testid="hero-input" />
            <div className="w-full h-48 border-2 border-dashed border-[var(--ag-border)] rounded grid place-items-center text-center p-4 hover:border-[var(--ag-gold)] transition-colors">
              <div>
                <ImageSquare size={36} weight="duotone" className="mx-auto text-[var(--ag-text-dim)]" />
                <div className="text-sm text-[var(--ag-text)] mt-2">Subir foto del local</div>
                <div className="text-[11px] text-[var(--ag-text-dim)] mt-1">Se usará como cabecera en tu página pública · 1200×600 recomendado · Máx 2 MB</div>
              </div>
            </div>
          </label>
        )}
      </div>

      {/* Social + Google Reviews */}
      <div className="ag-card p-6 space-y-4" data-testid="set-social-card">
        <div className="flex items-center gap-2 text-sm text-[var(--ag-text-dim)]"><Globe weight="duotone" /> Redes y Google Reviews</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">Instagram URL</Label>
            <Input value={form.instagram_url} onChange={(e)=>setForm({...form, instagram_url: e.target.value})}
              className="mt-1 rounded-sm" placeholder="https://instagram.com/tu_centro" data-testid="set-instagram" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Facebook URL</Label>
            <Input value={form.facebook_url} onChange={(e)=>setForm({...form, facebook_url: e.target.value})}
              className="mt-1 rounded-sm" placeholder="https://facebook.com/tu_centro" data-testid="set-facebook" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Google Place ID <span className="normal-case text-[var(--ag-text-dim)]">(para futura integración Google Reviews)</span></Label>
          <Input value={form.google_place_id} onChange={(e)=>setForm({...form, google_place_id: e.target.value})}
            className="mt-1 rounded-sm font-mono-tab" placeholder="ChIJN1t_tDeuEmsRUsoyG83frY4" data-testid="set-google-place-id" />
          <p className="text-[11px] text-[var(--ag-text-dim)] mt-1">Lo encuentras en <a href="https://developers.google.com/maps/documentation/places/web-service/place-id" target="_blank" rel="noreferrer" className="underline">Place ID Finder</a>.</p>
        </div>
      </div>

      {/* Gallery (trabajos detailing) */}
      <div className="ag-card p-6 space-y-4" data-testid="set-gallery-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-sm text-[var(--ag-text-dim)]"><ImageSquare weight="duotone" /> Galería de trabajos · {form.gallery.length}/12</div>
          {form.gallery.length < 12 && (
            <label className="cursor-pointer text-xs text-[var(--ag-gold)] hover:underline">
              <input type="file" accept="image/*,video/*" multiple
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  const remaining = 12 - form.gallery.length;
                  const toAdd = files.slice(0, remaining);
                  const items = [];
                  for (const f of toAdd) {
                    if (f.size > 2 * 1024 * 1024) { toast.error(`${f.name}: > 2 MB`); continue; }
                    items.push(await new Promise((r) => { const fr = new FileReader(); fr.onload = () => r(fr.result); fr.readAsDataURL(f); }));
                  }
                  setForm((f) => ({ ...f, gallery: [...f.gallery, ...items] }));
                  e.target.value = "";
                }}
                className="hidden" data-testid="set-gallery-input" />
              + Añadir imágenes o vídeos
            </label>
          )}
        </div>
        {form.gallery.length === 0 ? (
          <div className="text-xs text-[var(--ag-text-dim)] text-center py-6 border border-dashed border-[var(--ag-border)] rounded">
            Aún sin material. Sube fotos o vídeos de trabajos para destacar en tu ficha pública.
          </div>
        ) : (
          <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
            {form.gallery.map((u, i) => (
              <div key={`gal-${u.slice(0, 32)}-${i}`} className="relative aspect-square rounded overflow-hidden bg-[var(--ag-midnight)] border border-[var(--ag-border)]">
                {u.startsWith("data:video/") || /\.(mp4|webm)$/i.test(u) ? (
                  <video src={u} className="w-full h-full object-cover" muted />
                ) : (
                  <img src={u} alt={`Galería ${i + 1}`} className="w-full h-full object-cover" />
                )}
                <button type="button"
                  onClick={() => setForm((f) => ({ ...f, gallery: f.gallery.filter((_, idx) => idx !== i) }))}
                  className="absolute top-1 right-1 bg-red-500 text-white w-6 h-6 rounded grid place-items-center hover:bg-red-600"
                  data-testid={`gallery-remove-${i}`}>
                  <Trash size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Public booking URL */}
      <div className="ag-card p-6 space-y-4">
        <div className="flex items-center gap-2 text-sm text-[var(--ag-text-dim)]"><Globe weight="duotone" /> Reserva online pública</div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Slug del centro (URL pública)</Label>
          <Input value={form.slug} onChange={(e)=>setForm({...form, slug: e.target.value})} className="mt-1 rounded-sm font-mono-tab" placeholder="tu-centro" data-testid="set-slug" />
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <Input value={bookingUrl} readOnly className="rounded-sm font-mono-tab text-xs flex-1 min-w-0" data-testid="public-booking-url" />
          <div className="flex items-center gap-2 shrink-0">
            <Button onClick={copyUrl} variant="outline" className="rounded-sm flex-1 sm:flex-none"><Copy className="mr-1.5" size={14} /> Copiar</Button>
            <Button variant="outline" className="rounded-sm flex-1 sm:flex-none" onClick={()=>window.open(bookingUrl, "_blank")}>Abrir</Button>
          </div>
        </div>
        <p className="text-[11px] text-[var(--ag-text-dim)]">Comparte este enlace con tus clientes. Verán tu logo, dirección, catálogo y podrán reservar directamente en tu agenda.</p>
        {/* QR generator para imprimir en tarjetas/pegatinas */}
        <QrGenerator
          defaultUrl={bookingUrl}
          title="Código QR para tu reserva pública"
          subtitle="Imprímelo en tarjetas de visita, pegatinas para el cristal del taller o cartelería."
          brandColor={form.brand_color || "#0033A0"}
          allowEdit={false}
          hiResSize={2048}
          filenameBase={`autoglow-${form.slug || "centro"}`}
        />
        <div className="pt-2">
          <OpeningHoursDesigner
            value={form.opening_hours_per_day || {}}
            workingDays={form.working_days}
            fallbackStart={form.working_hours_start}
            fallbackEnd={form.working_hours_end}
            onChange={(perDay, workingDaysOut) => setForm((f) => ({
              ...f,
              opening_hours_per_day: perDay,
              working_days: workingDaysOut,
            }))}
          />
        </div>
      </div>

      {/* Comisión — solo si modo team */}
      {!isSolo && (
        <div className="ag-card p-6 space-y-4" data-testid="commission-section">
          <div className="text-sm text-[var(--ag-text-dim)]">Reparto de comisión titular / colaborador</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">% Titular</Label>
              <Input type="number" step="1" min={0} max={100} value={form.commission_titular} onChange={(e)=>onTit(e.target.value)} className="mt-1 rounded-sm tabular-nums" data-testid="set-tit" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">% Colaborador</Label>
              <Input type="number" step="1" min={0} max={100} value={form.commission_colaborador} onChange={(e)=>onCol(e.target.value)} className="mt-1 rounded-sm tabular-nums" data-testid="set-col" />
            </div>
          </div>
          <p className="text-[11px] text-[var(--ag-text-dim)]">Por defecto 40% / 60%. Estos porcentajes se aplican sobre la base imponible de cada servicio.</p>
        </div>
      )}
      {isSolo && (
        <div className="ag-card p-4 flex items-center gap-3 border-[var(--ag-aqua)]/30" data-testid="solo-mode-notice">
          <Sparkle size={18} weight="fill" className="text-[var(--ag-aqua-soft)]" />
          <div className="text-sm">
            <b className="text-[var(--ag-text)]">Modo Solo Titular activo.</b>
            <span className="text-[var(--ag-text-dim)]"> El reparto de comisión se mostrará cuando añadas un colaborador con un código de invitación.</span>
          </div>
        </div>
      )}

      <div className="ag-card p-6">
        <Label className="text-xs uppercase tracking-wider">Gastos operativos fijos medios (€/mes)</Label>
        <Input type="number" step="0.01" value={form.fixed_monthly_costs} onChange={(e)=>setForm({...form, fixed_monthly_costs: e.target.value})}
          className="mt-1 rounded-sm tabular-nums max-w-xs" data-testid="set-fixed" />
        <p className="text-[11px] text-[var(--ag-text-dim)] mt-1">Se descuenta para calcular el <b className="text-[var(--ag-text)]">Beneficio Neto Real</b>. Se prorratea según los días transcurridos del mes. Por defecto 1.050€.</p>
      </div>

      <div className="ag-card p-6">
        <Label className="text-xs uppercase tracking-wider">Email de notificaciones del titular</Label>
        <Input type="email" value={form.notification_email} onChange={(e)=>setForm({...form, notification_email: e.target.value})}
          className="mt-1 rounded-sm max-w-md" placeholder="reservas@tu-centro.com" data-testid="set-notif-email" />
        <p className="text-[11px] text-[var(--ag-text-dim)] mt-1">Recibirás aquí cada nueva cita reservada y los recordatorios 24h antes.</p>
      </div>

      {/* Sprint I · #12 · Datos del gestor (Asistente de presentación AEAT) */}
      <div className="ag-card p-6" data-testid="set-gestor-card">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)] font-semibold">Sprint I</span>
          <Label className="text-xs uppercase tracking-wider">Gestoría / Asesor fiscal</Label>
        </div>
        <p className="text-[11px] text-[var(--ag-text-dim)] mb-3">
          Configura el email de tu gestor para que puedas enviarle los modelos AEAT (303 · 111 · 115 · 130 · 390 · 190 · 180) con un solo click desde la página de Impuestos.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl">
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Nombre del gestor</Label>
            <Input value={form.gestor_name} onChange={(e)=>setForm({...form, gestor_name: e.target.value})}
              className="mt-1 rounded-sm" placeholder="Ana López · Gestoría XYZ" data-testid="set-gestor-name" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Email del gestor</Label>
            <Input type="email" value={form.gestor_email} onChange={(e)=>setForm({...form, gestor_email: e.target.value})}
              className="mt-1 rounded-sm" placeholder="gestor@gestoria.com" data-testid="set-gestor-email" />
          </div>
        </div>
      </div>

      <Button onClick={save} disabled={busy} className="ag-btn-gold rounded-sm h-11" data-testid="set-save">
        <FloppyDisk className="mr-1.5" weight="fill" /> {busy ? "Guardando…" : "Guardar cambios"}
      </Button>

      <ActiveSessionsCard api={api} role="user" />
      <PasskeysCard />

      {/* Sprint T-21 · Fase 1b · Push Web (arquitectura unificada) */}
      <NotificationPreferencesCard apiClient={api} variant="shop" />

      <SettingsExtensions role={user?.role} />

      {/* Sprint D · #9 · Modal de recorte/redimensionado de imágenes */}
      <ImageCropDialog
        open={cropState.open}
        src={cropState.src}
        aspectRatio={cropState.aspect}
        title={cropState.title || "Recortar imagen"}
        onConfirm={onCropConfirm}
        onCancel={onCropCancel}
      />
    </div>
  );
}
